import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { getCenterLocalTransport } from '../../../../api/centers/get-center-local-transport'
import { updateCenterLocalTransport } from '../../../../api/centers/update-center-local-transport'
import { useObserver } from '../../../../lib/contexts/observer'
import useTranslate from '../../../../lib/hooks/use-translate'
import { CurrencyEnum } from '../../../../lib/types/enums/common/currency.enum'
import { LocalTransportPricing } from '../../../../lib/types/models/local-transport-pricing/local-transport-pricing'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import EditableTable, { EditableTableColumn } from '../../../elements/editable-table'
import CenterLocalTransportFooter from '../../ol-common/ol-center-program/center-local-transport-footer'

type Props = {
  centerId: number
  currency?: CurrencyEnum
}

const CenterLocalPricingEditableTable = ({ centerId, currency }: Props) => {
  const [localTransport, setLocalTransport] = useState<LocalTransportPricing | null>(null)
  const { t } = useTranslate()
  const queryClient = useQueryClient()

  const { refetch } = useQuery({
    queryKey: ['center-local-transport', centerId],
    queryFn: async () => {
      const res = await getCenterLocalTransport(centerId)
      setLocalTransport(res)
      return res
    },
    enabled: !!centerId
  })

  const updateCenterLocalTransportMutation = useMutation({
    mutationFn: updateCenterLocalTransport,
    onSuccess: (data) => {
      setLocalTransport(data)
      queryClient.invalidateQueries({ queryKey: ['center-costs-view', centerId] })
    },
    onError: showAxiosError
  })

  useObserver({
    key: 'ol-center-activity-table',
    update: () => {
      refetch()
    }
  })

  const localTransportColumns: EditableTableColumn<LocalTransportPricing>[] = [
    {
      accessorKey: 'splitAge',
      header: t('ol.center.local.transport.table.split.age'),
      inputType: 'number',
      isEditable: true
    },
    {
      accessorKey: 'priceUnderSplitAge',
      header: `${t('ol.center.local.transport.table.price')} < ${localTransport?.splitAge ?? 'X'}`,
      isEditable: true,
      inputType: 'number',
      cell: ({ row }) =>
        row.original.priceUnderSplitAge ? (
          <Badge
            variant="primary"
            text={NumberFormatter.formatCurrency(row.original.priceUnderSplitAge, currency)}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'priceOverSplitAge',
      header: `${t('ol.center.local.transport.table.price')} > ${localTransport?.splitAge ?? 'X'}`,
      isEditable: true,
      inputType: 'number',
      cell: ({ row }) =>
        row.original.priceOverSplitAge ? (
          <Badge
            variant="primary"
            text={NumberFormatter.formatCurrency(row.original.priceOverSplitAge, currency)}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'numberOfFtrStudents',
      header: t('ol.center.local.transport.table.ftr.students')
    },
    {
      accessorKey: 'ftrAmount',
      header: t('ol.center.local.transport.table.ftr.amount'),
      cell: ({ row }) =>
        row.original.ftrAmount ? (
          <Badge
            variant="primary"
            text={NumberFormatter.formatCurrency(row.original.ftrAmount, currency)}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'numberOfFrenchLeaders',
      header: t('ol.center.local.transport.table.french.leaders'),
      inputType: 'number',
      isEditable: true
    },
    {
      accessorKey: 'frenchLeaderAmount',
      header: t('ol.center.local.transport.table.french.leaders.amount'),
      cell: ({ row }) =>
        row.original.frenchLeaderAmount ? (
          <Badge
            variant="primary"
            text={NumberFormatter.formatCurrency(row.original.frenchLeaderAmount, currency)}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'totalCost',
      header: t('ol.center.local.transport.table.total.cost'),
      cell: ({ row }) =>
        row.original.totalCost ? (
          <Badge
            variant="primary"
            text={NumberFormatter.formatCurrency(row.original.totalCost, currency)}
          />
        ) : (
          '-'
        )
    }
  ]

  return (
    <EditableTable<LocalTransportPricing>
      columns={localTransportColumns}
      isEditable
      onDataChange={(data) => {
        data.map((item) => {
          updateCenterLocalTransportMutation.mutate({
            id: centerId,
            priceOverSplitAge: item.priceOverSplitAge,
            priceUnderSplitAge: item.priceUnderSplitAge,
            numberOfFrenchLeaders: item.numberOfFrenchLeaders,
            splitAge: item.splitAge
          })

          setLocalTransport((prev) => {
            if (!prev) return null
            return {
              ...prev,
              splitAge: item.splitAge,
              priceOverSplitAge: item.priceOverSplitAge,
              priceUnderSplitAge: item.priceUnderSplitAge,
              numberOfFrenchLeaders: item.numberOfFrenchLeaders
            }
          })
        })
      }}
      data={localTransport ? [localTransport] : []}
      footer={<CenterLocalTransportFooter centerId={centerId} currency={currency} />}
    />
  )
}

export default CenterLocalPricingEditableTable
